"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "@/i18n";
import { FadeIn, FadeInStagger, FadeInStaggerChild } from "@/components/ui/AnimatedSection";
import { cn } from "@/lib/utils";

export function FAQ() {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const { t } = useTranslation();

    return (
        <div>
            <FadeIn>
                <h2 className="text-2xl font-bold text-text mb-8">
                    {t.faq.title}
                </h2>
            </FadeIn>

            <FadeInStagger className="space-y-3">
                {t.faq.items.map((item, i) => (
                    <FadeInStaggerChild key={i}>
                        <div className="rounded-xl border border-border bg-surface/50 overflow-hidden">
                            <button
                                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                                className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left cursor-pointer"
                                aria-expanded={openIndex === i}
                                aria-controls={`faq-answer-${i}`}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-text-muted shrink-0 w-20">
                                        {item.category}
                                    </span>
                                    <span className="text-sm font-medium text-text">
                                        {item.question}
                                    </span>
                                </div>
                                <svg
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    className={cn(
                                        "w-5 h-5 text-text-muted shrink-0 transition-transform duration-300",
                                        openIndex === i && "rotate-180"
                                    )}
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M5 8l5 5 5-5"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>

                            <AnimatePresence>
                                {openIndex === i && (
                                    <motion.div
                                        id={`faq-answer-${i}`}
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                                        className="overflow-hidden"
                                    >
                                        <p className="px-6 pb-4 text-sm text-text-secondary leading-relaxed pl-[104px]">
                                            {item.answer}
                                        </p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </FadeInStaggerChild>
                ))}
            </FadeInStagger>
        </div>
    );
}
